import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { jobsAPI } from '@/services/api';
import JobStatusBadge from '@/components/user/JobStatusBadge';
import EmptyState from '@/components/user/EmptyState';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CLIENT_ACTIVE_STATUSES, CLIENT_DONE_STATUSES, formatDate } from '@/utils/jobStatus';
import { toast } from 'sonner';
import { ChevronRight } from 'lucide-react';

const ClienteJobsPage = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await jobsAPI.list();
      setJobs(Array.isArray(data) ? data : data?.jobs || []);
    } catch {
      toast.error('Não foi possível carregar os jobs.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const activeJobs = useMemo(
    () => jobs.filter((j) => CLIENT_ACTIVE_STATUSES.includes(j.status)),
    [jobs]
  );
  const doneJobs = useMemo(
    () => jobs.filter((j) => CLIENT_DONE_STATUSES.includes(j.status)),
    [jobs]
  );

  const renderList = (list) => {
    if (loading) {
      return (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 w-full bg-white/10" />
          ))}
        </div>
      );
    }
    if (!list.length) {
      return (
        <EmptyState
          title="Nenhum job nesta aba"
          description="Quando houver entregas ou jobs em andamento, eles aparecerão aqui."
        />
      );
    }
    return (
      <ul className="space-y-3">
        {list.map((job) => (
          <li key={job.id}>
            <button
              type="button"
              onClick={() => navigate(`/cliente/jobs/${job.id}`)}
              className="w-full text-left rounded-xl border border-white/10 bg-white/5 p-4 hover:bg-white/10 transition-colors flex items-center justify-between gap-3"
            >
              <div className="min-w-0">
                <p className="font-medium truncate">{job.titulo || `Job ${job.id?.slice(0, 8)}`}</p>
                <p className="text-xs text-white/50 mt-1">{formatDate(job.data_gravacao || job.created_at)}</p>
                {job._delivery?.files_count > 0 && (
                  <p className="text-xs text-white/60 mt-1">
                    {job._delivery.files_count} arquivo(s)
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <JobStatusBadge status={job.status} />
                <ChevronRight className="h-4 w-4 text-white/40" />
              </div>
            </button>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Meus jobs</h1>
      <Tabs defaultValue="active">
        <TabsList className="bg-white/10 border border-white/20">
          <TabsTrigger value="active">Em andamento</TabsTrigger>
          <TabsTrigger value="done">Concluídos</TabsTrigger>
        </TabsList>
        <TabsContent value="active" className="mt-4">
          {renderList(activeJobs)}
        </TabsContent>
        <TabsContent value="done" className="mt-4">
          {renderList(doneJobs)}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ClienteJobsPage;
